"use client";

// ─────────────────────────────────────────────────────────────────────────────
// BlogBuddyTour — trade-ideas leg of the cross-page onboarding chain.
//
// Activates on `/blog?onboarding_blog=1` (set by the home tour's
// "trade_ideas" stage when the user clicks Browse more on the Top
// Trade Ideas pill).
//
// Four beats:
//   1. Intro spotlight on the case-study grid (no dim, no scroll) —
//      explains what these cards are.
//   2. Highlight the filter bar — frames it as a way to narrow down.
//   3. Click_target on the Options filter pill — flips the grid to
//      options-only strategies, which surfaces the whale-watching card.
//   4. Click_target on the whale-watching case-study card — the
//      natural Link navigates to /blog/{slug}?onboarding_blog_article=1,
//      where BlogArticleBuddyTour picks up the next leg.
//
// Mirrors the AutomationsBuddyTour pattern (URL-driven, exit cleans
// up the param, no completion redirect — the natural click on the
// click_target card already navigates to the article page).
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { BuddyOverlay, type BuddyBeat } from "./BuddyOverlay";
import { useClientBuddyScript } from "./useClientBuddyScript";
import {
  BLOG_TOUR_QUERY_PARAM,
  isBlogTourVisit,
} from "@/lib/onboarding-constants";

export function BlogBuddyTour() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const shouldRun = isBlogTourVisit(searchParams);

  const beats = useMemo<BuddyBeat[]>(
    () => [
      {
        beatId: "blog_grid_intro",
        trigger: { type: "client" },
        copy: {
          title: "Proven guides",
          body:
            "Proven guides and writeups from Xynth power users and the Xynth team, showing how they leverage AI to make money in markets and how you can too.",
          cta: "Next",
        },
        anchor: { kind: "css", value: "[data-tour='trades-grid']" },
        highlightOnly: true,
        hideSpotlightRing: true,
        noScroll: true,
        bubblePlacement: "topCenter",
        bubbleOffset: { x: 260 },
      },
      {
        beatId: "blog_filter_bar_intro",
        trigger: { type: "client" },
        copy: {
          title: "Narrow it down",
          body:
            "Filter the grid by the asset class you trade. Options, stocks, earnings plays, swing setups.",
          cta: "Next",
        },
        anchor: { kind: "css", value: "[data-tour='blog-filter-bar']" },
        highlightOnly: true,
        noScroll: true,
        bubblePlacement: "anchorBelow",
      },
      {
        beatId: "blog_click_options_filter",
        trigger: { type: "client" },
        copy: {
          title: "Tap Options",
          body:
            "Click the Options pill to filter the grid down to options strategies.",
        },
        anchor: { kind: "css", value: "[data-tour='blog-filter-options']" },
        action: { kind: "click_target" },
        highlightOnly: true,
        noScroll: true,
        bubblePlacement: "anchorBelow",
      },
      {
        beatId: "blog_open_whale_card",
        trigger: { type: "client" },
        copy: {
          title: "Open Whale Watching",
          body:
            "Open this guide to see how the strategy works.",
        },
        anchor: { kind: "css", value: "[data-tour='trades-first-card']" },
        action: { kind: "click_target" },
        highlightOnly: true,
        noScroll: true,
        bubblePlacement: "anchor",
      },
    ],
    [],
  );

  const stripQueryParam = useMemo(
    () => () => {
      try {
        const next = new URLSearchParams(searchParams?.toString() || "");
        next.delete(BLOG_TOUR_QUERY_PARAM);
        const search = next.toString();
        const path = window.location.pathname + (search ? `?${search}` : "");
        router.replace(path, { scroll: false });
      } catch {
        /* URL mutation is best-effort. */
      }
    },
    [router, searchParams],
  );

  const { beat, onContinue, onExit, done } = useClientBuddyScript(
    shouldRun ? beats : [],
    {
      onComplete: () => {
        // No-op: the click_target on the first card already navigates
        // to the article page (with ?onboarding_blog_article=1) via
        // the natural Link click. BlogArticleBuddyTour takes over
        // there.
      },
      onExit: () => {
        stripQueryParam();
      },
    },
  );

  if (!shouldRun || done) return null;

  return <BuddyOverlay beat={beat} onContinue={onContinue} onExit={onExit} />;
}
